/**
 * Shared byte→LoadedAsset decoding for browser AssetSources (live runtime's
 * `browserAssetSource` and the test runner's `createTestAssetSource`).
 * Images are decoded to an HTMLImageElement via a blob URL so game code can
 * ctx.drawImage() synchronously after preload; everything else stays bytes.
 */
import type { LoadedAsset } from '@gotti/core';
import { getAssetCategory, isSupportedAssetType } from '@gotti/sandbox-shared';
import { getApiBaseUrl } from '../persistence/client';

function decodeImage(bytes: Uint8Array, mimeType: string, path: string): Promise<{ image: HTMLImageElement; url: string }> {
  const blob = new Blob([bytes], { type: mimeType });
  const url = URL.createObjectURL(blob);
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ image, url });
    image.onerror = () => { URL.revokeObjectURL(url); reject(new Error(`Failed to decode image "${path}" (${mimeType})`)); };
    image.src = url;
  });
}

/** Decode raw bytes into the LoadedAsset shape AssetManager preloads. */
export async function bytesToLoadedAsset(path: string, mimeType: string, bytes: Uint8Array): Promise<LoadedAsset> {
  // `mimeType` arrives off the wire as an arbitrary string, but
  // `getAssetCategory` is `(mimeType: AssetMimeType)` and its body is a bare
  // `ASSET_TYPES[mimeType].category` — an unrecognised type would have thrown a
  // TypeError right here mid-preload instead of falling through. The narrowing
  // guard is what the docblock above already promises ("everything else stays
  // bytes").
  if (isSupportedAssetType(mimeType) && getAssetCategory(mimeType) === 'image') {
    const { image, url } = await decodeImage(bytes, mimeType, path);
    return { path, mimeType, bytes, image, url };
  }
  return { path, mimeType, bytes };
}

/**
 * Fetch an asset's raw bytes by reference: a direct `url`, else the backend's
 * content-addressed endpoint (`GET /api/assets/:hash?type=<mime>&content=true`).
 * Single home for that endpoint contract — used by both the test asset source
 * (TestAssetRef resolution) and the asset store's generated-asset fold.
 */
export async function fetchAssetContentBytes(
  ref: { path: string; hash: string; mimeType: string; url?: string },
  caller: string,
): Promise<Uint8Array> {
  const url =
    ref.url ??
    `${getApiBaseUrl()}/api/assets/${ref.hash}?type=${encodeURIComponent(ref.mimeType)}&content=true`;
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`${caller}: failed to fetch asset "${ref.path}" (${res.status} from ${url})`);
  }
  return new Uint8Array(await res.arrayBuffer());
}
